import React from "react";
import { STYLES, C, Section, Steps, Consult } from "./ffs/FfsSubpage";
import { PUBLICATIONS, BOOK_CHAPTERS } from "../publications";
import { LIVE_PROVIDERS, providerPath } from "../providers";
import IMG_HOPKINS from "../assets/hopkins.jpg";
import IMG_SALVADOR from "../assets/el-salvador-fellowship.jpg";
import IMG_JACKSON from "../assets/jackson-memorial.jpg";

/* ─── About Dr. Ovadia ───
   The surgeon's own page, at /about-dr-ovadia. This is the one place his full biography lives: the homepage
   keeps a short introduction and links here, and the Physician structured data (src/App.js) names this page
   as his address, so search engines and AI crawlers have one canonical page to tie his name, training,
   affiliations and publications together.

   Routed, saved and listed in the sitemap through PAGE_ROUTES in src/App.js. Uses the shared sub-page
   styles (src/pages/ffs/FfsSubpage.js) so it reads as part of the same family as the procedure pages.

   Nothing in this file may read `window` or `document` while rendering, or the publish-time render breaks. */

export const ABOUT_PATH = "/about-dr-ovadia";
export const ABOUT_TITLE = "About Dr. Steven Ovadia, MD | Plastic Surgeon, NYC & NJ";
export const ABOUT_DESCRIPTION = "Dr. Steven A. Ovadia is a board certified, double fellowship-trained plastic and reconstructive surgeon (Johns Hopkins; UT Austin) in NYC, Jersey City, and Millburn, NJ.";

const JUMPS = [
  { id: "overview", label: "Overview" },
  { id: "training", label: "Training" },
  { id: "practice", label: "Practice" },
  { id: "affiliations", label: "Hospitals" },
  { id: "research", label: "Research" },
  ...(LIVE_PROVIDERS.length ? [{ id: "team", label: "The team" }] : []),
];

const FACTS = [
  { big: "2", small: "fellowships after residency: gender surgery and microsurgery; craniofacial and pediatric plastic surgery" },
  { big: String(PUBLICATIONS.length), small: "peer-reviewed publications, plus five book chapters" },
  { big: "11", small: "hospitals in New York and New Jersey where he holds surgical privileges" },
  { big: "3", small: "offices: Lower Manhattan, Jersey City, and Millburn" },
];

/* Chronological. The surgeon's own training, in the order it happened. */
const TRAINING = [
  { title: "New York University", text: "Undergraduate degree. Member of the Presidential Honors Scholar Program and recipient of the NYU Founders Day Award." },
  { title: "University of Miami Miller School of Medicine", text: "Doctor of Medicine, 2014. Inducted into the Alpha Omega Alpha Honor Medical Society." },
  { title: "Plastic Surgery Residency — University of Miami / Jackson Memorial Hospital", text: "Six years of integrated plastic surgery training at one of the busiest trauma and reconstruction centers in the country." },
  { title: "Fellowship — Johns Hopkins University", text: "Complex gender surgery and microsurgery: facial feminization, top surgery, vaginoplasty and phalloplasty, and free-flap reconstruction." },
  { title: "Fellowship — UT Austin Dell Medical School / Dell Children's Medical Center", text: "Craniofacial and pediatric plastic surgery: cleft lip and palate, craniosynostosis, facial trauma, and orthognathic surgery." },
  { title: "Director, Rutgers Center for Transgender Health", text: "Led the center until the end of 2025, before founding his own private practice." },
];

/* What he does, linked to the pages that go into each. Gender-affirming surgery first, by his preference. */
const FOCUS = [
  { title: "Gender-affirming surgery", text: "Facial feminization, top surgery, body feminization, and vaginoplasty, with individualized surgical planning for each patient.", links: [["/facial-feminization-surgery", "Facial feminization"], ["/top-surgery", "Top surgery"], ["/body-feminization-surgery", "Body feminization"]] },
  { title: "Breast reconstruction", text: "Every form of reconstruction after mastectomy or lumpectomy: implant-based, DIEP and other flaps, oncoplastic breast-conserving surgery, nipple reconstruction, and revision of earlier reconstruction.", links: [["/procedures/breast-reconstruction", "Breast reconstruction"], ["/implant-based-breast-reconstruction", "Implant-based"], ["/oncoplastic-breast-surgery", "Oncoplastic"], ["/nipple-reconstruction", "Nipple reconstruction"]] },
  { title: "Craniofacial and facial surgery", text: "Craniofacial training for children and adults carries into his facial surgery: facelift and neck lift, rhinoplasty, blepharoplasty, lip lift, fat grafting, and facial trauma reconstruction.", links: [["/facelift", "Facelift"], ["/rhinoplasty", "Rhinoplasty"], ["/blepharoplasty", "Blepharoplasty"], ["/procedures/craniofacial", "Craniofacial surgery"]] },
  { title: "Breast and body", text: "Breast augmentation, lift, reduction, implant exchange and revision; liposuction, tummy tuck, arm lift, lipedema surgery, and body contouring after weight loss.", links: [["/breast-reduction", "Breast reduction"], ["/breast-augmentation", "Breast augmentation"], ["/lipedema", "Lipedema"], ["/tummy-tuck", "Tummy tuck"]] },
];

const HOSPITALS = [
  ["Lenox Hill Hospital", "New York, NY"],
  ["Jersey City Medical Center", "Jersey City, NJ"],
  ["Hudson Regional Hospital", "Secaucus, NJ"],
  ["University Hospital", "Newark, NJ"],
  ["Newark Beth Israel Medical Center", "Newark, NJ"],
  ["Cooperman Barnabas Medical Center", "Livingston, NJ"],
  ["Overlook Medical Center", "Summit, NJ"],
  ["Morristown Medical Center", "Morristown, NJ"],
  ["Clara Maass Medical Center", "Belleville, NJ"],
  ["Hoboken University Medical Center", "Hoboken, NJ"],
  ["Bayonne Medical Center", "Bayonne, NJ"],
];

const MEMBERSHIPS = [
  ["American Board of Plastic Surgery", "Board certified", "https://www.abplasticsurgery.org"],
  ["American Society of Plastic Surgeons", "Member", "https://www.plasticsurgery.org/md/Steven-Ovadia-MD.html"],
  ["World Professional Association for Transgender Health", "Member (USPATH)", "https://www.wpath.org"],
  ["GLMA: Health Professionals Advancing LGBTQ+ Equality", "Member", "https://www.glma.org"],
  ["Alpha Omega Alpha Honor Medical Society", "Inducted 2014", null],
];

const EXTRA_STYLES = `
  .ab-hero-grid { display: grid; grid-template-columns: 1fr 300px; gap: clamp(32px, 5vw, 64px); align-items: end; }
  .ab-hero-photo { margin: 0; }
  .ab-hero-photo img { width: 100%; height: auto; display: block; border-top: 3px solid ${C.gold}; }
  .ab-hero-photo figcaption { font-family: var(--body); font-size: 12.5px; color: rgba(255,255,255,0.6); margin-top: 10px; line-height: 1.5; }
  .ab-badges { display: flex; align-items: center; gap: 28px; flex-wrap: wrap; margin-top: 28px; }
  .ab-badges a { display: block; }
  .ab-badges img { display: block; height: auto; }
  .ab-two { display: grid; grid-template-columns: 1fr 1fr; gap: clamp(28px, 4vw, 48px); align-items: start; }
  .ab-photos { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .ab-photos figure { margin: 0; }
  .ab-photos .ab-photo-wide { grid-column: 1 / -1; }
  .ab-photos img { width: 100%; height: auto; display: block; border-radius: 4px; }
  .ab-photos figcaption { font-family: var(--body); font-size: 13px; color: ${C.textLight}; font-style: italic; text-align: center; margin-top: 8px; line-height: 1.5; }
  .ab-grid-3 { display: grid; grid-template-columns: 1fr 0.65fr 1fr; gap: 14px; align-items: start; margin-top: 8px; }
  .ab-grid-3 figure { margin: 0; }
  .ab-grid-3 img { width: 100%; height: auto; display: block; border-radius: 4px; }
  .ab-grid-3 figcaption { font-family: var(--body); font-size: 13px; color: ${C.textLight}; font-style: italic; text-align: center; margin-top: 10px; line-height: 1.5; }
  .ab-hosp { list-style: none; display: grid; grid-template-columns: 1fr 1fr; gap: 0 32px; margin-top: 8px; }
  .ab-hosp li { font-family: var(--body); font-size: 15.5px; line-height: 1.5; color: ${C.textBody}; padding: 12px 0; border-top: 1px solid ${C.border}; display: flex; justify-content: space-between; gap: 12px; }
  .ab-hosp li span { color: ${C.textLight}; font-size: 14px; white-space: nowrap; }
  .ab-mem { list-style: none; margin-top: 8px; }
  .ab-mem li { font-family: var(--body); font-size: 15.5px; line-height: 1.5; color: ${C.textBody}; padding: 12px 0; border-top: 1px solid ${C.border}; display: flex; justify-content: space-between; gap: 12px; }
  .ab-mem li a { color: ${C.navy}; font-weight: 600; text-decoration: underline; text-decoration-color: ${C.gold}; text-decoration-thickness: 1.5px; text-underline-offset: 4px; }
  .ab-mem li span { color: ${C.goldDark}; font-size: 13px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; white-space: nowrap; }
  .ab-links { display: flex; flex-wrap: wrap; gap: 8px 14px; margin-top: 14px; }
  .ab-links a { font-family: var(--body); font-size: 13.5px; font-weight: 600; color: ${C.navy}; text-decoration: none; border-bottom: 1.5px solid ${C.gold}; padding-bottom: 2px; }
  .ab-links a:hover { color: ${C.goldDark}; }
  .ab-pubs { list-style: none; margin-top: 8px; }
  .ab-pubs li { padding: 14px 0; border-top: 1px solid ${C.border}; }
  .ab-pubs li a, .ab-pubs li .ab-pub-title { font-family: var(--body); font-size: 15.5px; line-height: 1.55; color: ${C.navy}; text-decoration: none; display: block; }
  .ab-pubs li a:hover { color: ${C.goldDark}; }
  .ab-pubs li .ab-pub-meta { font-family: var(--body); font-size: 13px; color: ${C.textLight}; font-style: italic; margin-top: 3px; }
  .ab-sub { font-family: var(--body); font-size: 12.5px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: ${C.goldDark}; margin: 36px 0 6px; }
  .ab-sub:first-of-type { margin-top: 0; }
  .ab-chapters { list-style: none; margin-top: 6px; }
  .ab-chapters li { font-family: var(--body); font-size: 14.5px; line-height: 1.6; color: ${C.textMuted}; padding: 6px 0 6px 18px; position: relative; }
  .ab-chapters li::before { content: ""; position: absolute; left: 0; top: 16px; width: 8px; height: 2px; background: ${C.gold}; }
  .ab-team { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .ab-team a { display: block; background: ${C.white}; border: 1px solid ${C.border}; border-top: 3px solid ${C.gold}; text-decoration: none; transition: background 0.3s; }
  .ab-team a:hover { background: ${C.bgAlt}; }
  .ab-team img, .ab-team .ab-team-initials { width: 100%; aspect-ratio: 4 / 5; object-fit: cover; display: block; }
  .ab-team .ab-team-initials { display: flex; align-items: center; justify-content: center; background: ${C.navy}; font-family: var(--head); font-size: 64px; color: ${C.gold}; letter-spacing: 3px; }
  .ab-team-body { padding: 18px 20px 22px; }
  .ab-team h3 { font-family: var(--head); font-size: 22px; font-weight: 600; color: ${C.navy}; line-height: 1.25; margin: 0 0 4px; }
  .ab-team p { font-family: var(--body); font-size: 13px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: ${C.goldDark}; margin: 0 0 10px; }
  .ab-team span { font-family: var(--body); font-size: 14px; color: ${C.navy}; font-weight: 600; border-bottom: 1.5px solid ${C.gold}; padding-bottom: 2px; }
  @media (max-width: 860px) {
    .ab-team { grid-template-columns: 1fr 1fr; }
    .ab-hero-grid { grid-template-columns: 1fr; }
    .ab-hero-photo { max-width: 260px; }
    .ab-two, .ab-hosp { grid-template-columns: 1fr; }
    .ab-grid-3 { grid-template-columns: 1fr 1fr; }
    .ab-grid-3 figure:last-child { grid-column: 1 / -1; }
    .ab-hosp li, .ab-mem li { flex-direction: column; gap: 2px; }
  }
`;

export default function AboutDrOvadia() {
  return (
    <main id="main-content" className="fs" style={{ background: C.bg }}>
      <style>{STYLES + EXTRA_STYLES}</style>

      <header className="fs-hero">
        <div className="fs-wrap">
          <div className="ab-hero-grid">
            <div>
              <p className="fs-eyebrow" style={{ color: C.gold }}>About the surgeon</p>
              <h1>Steven A. Ovadia, MD</h1>
              <div className="fs-stripe" aria-hidden="true"><span style={{ background: C.gold }} /></div>
              <p className="fs-hero-lede">Board certified plastic and reconstructive surgeon, fellowship-trained in both gender-affirming surgery and microsurgery (Johns Hopkins) and craniofacial and pediatric plastic surgery (UT Austin / Dell Children's). Dr. Ovadia founded Ovadia Plastic Surgery after serving as Director of the Rutgers Center for Transgender Health, and sees patients in Lower Manhattan, Jersey City, and Millburn, New Jersey.</p>
              <div className="fs-btns">
                <a className="fs-btn fs-btn-gold" href="#consult">Request a consultation</a>
                <a className="fs-btn fs-btn-line" href="/#procedures">See procedures</a>
              </div>
            </div>
            <figure className="ab-hero-photo">
              <img src="/headshot.png" width="855" height="1115" alt="Dr. Steven Ovadia, board certified plastic surgeon" />
            </figure>
          </div>
          <div className="fs-facts">
            {FACTS.map((f) => <div key={f.big + f.small} className="fs-fact"><b>{f.big}</b><span>{f.small}</span></div>)}
          </div>
        </div>
      </header>

      <nav className="fs-jump" aria-label="On this page">
        <div className="fs-wrap fs-jump-in">
          {JUMPS.map((j) => <a key={j.id} href={`#${j.id}`}>{j.label}</a>)}
        </div>
      </nav>

      <Section id="overview" eyebrow="Overview" title="Board certified, double fellowship-trained">
        <div className="ab-two">
          <div>
            <p className="fs-p">Dr. Ovadia is a double fellowship-trained plastic and reconstructive surgeon and craniofacial surgeon, board certified by the American Board of Plastic Surgery. He completed his plastic surgery residency at the University of Miami and Jackson Memorial Hospital, then two fellowships: complex gender surgery and microsurgery at Johns Hopkins, and craniofacial and pediatric plastic surgery at UT Austin and Dell Children's Medical Center.</p>
            <p className="fs-p">Following his fellowships he served as Director of the Center for Transgender Health at Rutgers University until the end of 2025, before founding his own private practice, Ovadia Plastic Surgery, with offices in New York City and New Jersey.</p>
            <p className="fs-p">With fellowship training in two surgical subspecialties, Dr. Ovadia brings a wide range of expertise to his patients. His practice spans breast reconstruction after cancer, gender-affirming surgery, lipedema surgery, facial trauma and complex facial reconstruction, pediatric plastic surgery, and cosmetic surgery of the face, breast, and body.</p>
            <div className="ab-badges">
              <a href="https://www.abplasticsurgery.org" target="_blank" rel="noopener noreferrer" style={{ width: "96px" }}>
                <img src="/badges/abps-certified-stacked.png" width="800" height="870" alt="Certified by The American Board of Plastic Surgery" loading="lazy" style={{ width: "100%" }} />
              </a>
              <a href="https://www.plasticsurgery.org/md/Steven-Ovadia-MD.html" target="_blank" rel="noopener noreferrer" style={{ width: "140px" }}>
                <img src="/badges/asps-member.png" width="600" height="534" alt="Member, American Society of Plastic Surgeons" loading="lazy" style={{ width: "100%" }} />
              </a>
            </div>
          </div>
          <div className="ab-photos">
            <figure className="ab-photo-wide">
              <img src={IMG_SALVADOR} width="952" height="826" alt="Dr. Ovadia operating alongside his craniofacial fellowship director on a surgical mission in El Salvador" loading="lazy" />
              <figcaption>Craniofacial fellowship — operating with his fellowship director on a mission in El Salvador</figcaption>
            </figure>
            <figure>
              <img src={IMG_HOPKINS} width="500" height="500" alt="Dr. Ovadia at Johns Hopkins School of Medicine" loading="lazy" />
              <figcaption>Fellowship — Johns Hopkins</figcaption>
            </figure>
            <figure>
              <img src={IMG_JACKSON} width="500" height="376" alt="Dr. Ovadia at Jackson Memorial Hospital" loading="lazy" />
              <figcaption>Residency — Jackson Memorial Hospital</figcaption>
            </figure>
          </div>
        </div>
      </Section>

      <Section id="training" eyebrow="Education and training" title="Where he trained" alt intro={["From New York to Miami, Baltimore, and Austin, and back to New York."]}>
        <div className="fs-narrow">
          <Steps items={TRAINING} />
        </div>
      </Section>

      <Section id="practice" eyebrow="The practice" title="What Dr. Ovadia does" intro={["Each area below has its own pages on this site, written from how he actually plans and performs the surgery."]}>
        <div className="fs-cards">
          {FOCUS.map((f) => (
            <div key={f.title} className="fs-card">
              <h3>{f.title}</h3>
              <p>{f.text}</p>
              <div className="ab-links">
                {f.links.map(([href, label]) => <a key={href} href={href}>{label} →</a>)}
              </div>
            </div>
          ))}
        </div>
        <p className="fs-note fs-narrow">Dr. Ovadia also performs lymphedema surgery, silicone removal, scar revision, skin cancer reconstruction, and facial trauma reconstruction. <a href="/procedures/additional">See additional procedures</a>, or <a href="/#contact">contact the office</a> about anything not listed.</p>
      </Section>

      <Section id="affiliations" eyebrow="Affiliations" title="Hospitals and professional societies" alt intro={["Dr. Ovadia operates at his Lower Manhattan office and holds surgical privileges at hospitals across New York City and northern New Jersey, so surgery can be scheduled at whichever facility suits the patient and the procedure."]}>
        <div className="ab-two">
          <div>
            <p className="ab-sub">Hospital privileges</p>
            <ul className="ab-hosp">
              {HOSPITALS.map(([name, place]) => <li key={name}>{name}<span>{place}</span></li>)}
            </ul>
          </div>
          <div>
            <p className="ab-sub">Certification and memberships</p>
            <ul className="ab-mem">
              {MEMBERSHIPS.map(([name, role, url]) => (
                <li key={name}>{url ? <a href={url} target="_blank" rel="noopener noreferrer">{name}</a> : name}<span>{role}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section id="research" eyebrow="Research" title="Publications" intro={[`Dr. Ovadia has authored ${PUBLICATIONS.length} peer-reviewed publications on gender-affirming surgery, breast surgery, nerve reconstruction, craniofacial surgery, surgical training, and the costs and outcomes of plastic surgery care, along with five book chapters on eyelid reconstruction. Titles link to PubMed or the publisher.`]}>
        <div className="fs-narrow">
          <p className="ab-sub">Peer-reviewed publications</p>
          <ul className="ab-pubs">
            {PUBLICATIONS.map((p) => (
              <li key={p.title}>
                {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.title}</a> : <span className="ab-pub-title">{p.title}</span>}
                <div className="ab-pub-meta">{p.journal}, {p.year}</div>
              </li>
            ))}
          </ul>
          <p className="ab-sub">Book chapters</p>
          <p className="fs-p" style={{ marginBottom: 0 }}>{BOOK_CHAPTERS.book}</p>
          <ul className="ab-chapters">
            {BOOK_CHAPTERS.chapters.map((b) => <li key={b.chapter}>{b.chapter}: {b.title}</li>)}
          </ul>
          <div className="ab-links" style={{ marginTop: "24px" }}>
            <a href="https://scholar.google.com/citations?user=iCr3DZ0AAAAJ" target="_blank" rel="noopener noreferrer">Google Scholar profile →</a>
            <a href="https://pubmed.ncbi.nlm.nih.gov/?term=Ovadia+SA%5BAuthor%5D" target="_blank" rel="noopener noreferrer">PubMed →</a>
          </div>
        </div>
      </Section>


      {LIVE_PROVIDERS.length > 0 && (
        <Section id="team" eyebrow="The team" title="Who else you will meet" alt narrow={false} intro={["Dr. Ovadia plans your surgery and is your surgeon. The providers below work alongside him in the office, from your first visit through recovery."]}>
          <div className="ab-team">
            {LIVE_PROVIDERS.map((p) => (
              <a key={p.slug} href={providerPath(p)}>
                {p.photo ? <img src={p.photo} alt={p.photoAlt || p.name} loading="lazy" /> : <div className="ab-team-initials" aria-hidden="true">{p.firstName[0]}{p.lastName[0]}</div>}
                <div className="ab-team-body"><h3>{p.name}</h3><p>{p.role}</p><span>Meet {p.firstName} →</span></div>
              </a>
            ))}
          </div>
        </Section>
      )}

      <Consult text="Consultations with Dr. Ovadia are unhurried: he examines you himself, goes over the options that make sense for you, and explains how your surgery and recovery would be planned." />
    </main>
  );
}
